/* eslint-disable operator-linebreak */
/* eslint-disable @typescript-eslint/no-unused-vars */
/* eslint-disable no-param-reassign */

import { ReactElement, useState, useEffect, useRef } from 'react';
import { Routes, Route, useNavigate, useParams, useLocation } from 'react-router-dom';
import { Formik, Form, FormikProps, validateYupSchema, getIn } from 'formik';
import { defaultForm, getReceiptPayload, getSubmissionPayload, iExtensionFormValues, ExtensionSchema, extensionMetaData } from '@services/ExtensionService';
import { FormContainer } from '@pages';
import ReactGA from 'react-ga';
import { FormNavFooter, FormLoading, Alert, ProgressBar } from '@components';
import { FormType } from '@constants';
import { iToolTips, iCountry, iFormNavActions, iApiError, iPartialFormData } from '@interfaces/interfaces';
import { getBasicInfo, getCountryList, getTooltips, savePartialForm, getPartialForm, submitSavedPartialForm } from '@api';
import { navigateToError, navigateToNextStep, getStringIndex, setFormDataValues, setCurrentFormikValues, getDateForValidateDebtor, navToError } from '@utils';
import ErrorContent from '@components/Content/AlertContent/ErrorContent';
import { ExtensionStep0, ExtensionStep1, ExtensionStep2, ExtensionStep3, ExtensionStep4, ExtensionStep5, ExtensionStep6, ExtensionStep7, ExtensionStep8, ExtensionStep9, ExtensionStep10, ExtensionStep11, ExtensionStep12, ExtensionStep15, ExtensionStep16, ExtensionErrorPage } from '.';
import VicFinesContext from '../VicFinesFormContext';
import IssueIcon from '../../../images/issue.svg';

interface ObligationParams {
  ob: string;
}

const { REACT_APP_SITECORE_API_ENDPOINT: API_URL, REACT_APP_PATH_EXTENSION: PATH_EXTENSION } = process.env;

function Extension(): ReactElement {
  const location = useLocation();
  const navigate = useNavigate();
  const pathElementArray = location.pathname.split('/');
  const stepIndex = getStringIndex(pathElementArray, 'step');
  const stepIndexDiff = stepIndex - pathElementArray.length;
  // example returned [step0,3224242] or [step0]
  const stepArray = pathElementArray.slice(stepIndexDiff);
  // example returned 0
  const stepIndexInPath = getStringIndex(stepArray, 'step');
  // always get the element contain step like step0
  const getStepInPath = stepArray[stepIndexInPath];
  const pathNameLessLastSegment = stepIndex ? location.pathname.split('/').slice(0, stepIndexDiff).join('/') : location.pathname;
  const currentStep = location.pathname === `${PATH_EXTENSION}` || location.pathname === `${PATH_EXTENSION}/` ? 'step0' : `${getStepInPath}`;

  const [loading, setLoading] = useState(true);
  const [countryList, setCountryList] = useState<iCountry[]>([]);
  const [tooltips, setTooltips] = useState<iToolTips>({} as iToolTips);
  const [apiError, setApiError] = useState<iApiError>({} as iApiError);
  const formikValuesRef = useRef({} as FormikProps<iExtensionFormValues>);
  const [activeError, setActiveError] = useState(false);
  type obligationNum = {
    ob: string;
  };
  const { ob } = useParams<obligationNum>();
  useEffect(() => {
    const fetchData = async (): Promise<void> => {
      try {
        const [countryResults, tooltipResults] = await Promise.all([getCountryList(), getTooltips('fines.ExtensionForm')]);
        setCountryList(countryResults);
        setTooltips(tooltipResults);
        setLoading(false);
      } catch (error) {
        navigateToError();
      }
    };
    fetchData();
    const handlePopstate = () => {
        const nextLocation = window.location.pathname;
        const isSubmitted = formikValuesRef.current && formikValuesRef.current.values ? formikValuesRef.current.values?.submitted : false;
        if (nextLocation === `${PATH_EXTENSION}/step9` || isSubmitted) {
          navigate({
            pathname: `${PATH_EXTENSION}/step10`,
            search: window.location.search,
          });
         }
         // back button logic on broswer
        if (nextLocation === `${PATH_EXTENSION}/step15` || nextLocation === `${PATH_EXTENSION}/step3` || nextLocation === `${PATH_EXTENSION}/step2`) {
          formikValuesRef.current.setFieldValue('OBLIGATION_NUMBER', '');
          formikValuesRef.current.setFieldTouched('OBLIGATION_NUMBER');
          formikValuesRef.current.setFieldValue('OFFENCE_DATE', '');
          formikValuesRef.current.setFieldTouched('OFFENCE_DATE');
          formikValuesRef.current.setFieldValue('HEARING_DATE', '');
          formikValuesRef.current.setFieldTouched('HEARING_DATE');
          formikValuesRef.current.setFieldValue('DRIVER_LICENCE', '');
          formikValuesRef.current.setFieldTouched('DRIVER_LICENCE');
          formikValuesRef.current.setFieldValue('VEHICLE_REGISTRATION', '');
          formikValuesRef.current.setFieldTouched('VEHICLE_REGISTRATION');
          formikValuesRef.current.setFieldValue('DOB', '');
          formikValuesRef.current.setFieldTouched('DOB');
          if (nextLocation === `${PATH_EXTENSION}/step3`) {
            formikValuesRef.current.setFieldValue('SaveYesNo', 'false');
          }
          navigate({
            pathname: `${PATH_EXTENSION}/step1`,
            search: window.location.search,
          }, { replace: true });
        }
        if (nextLocation === `${PATH_EXTENSION}/step1`) {
          formikValuesRef.current.setFieldValue('OBLIGATION_NUMBER', '');
          formikValuesRef.current.setFieldTouched('OBLIGATION_NUMBER');
        }
      };
      window.addEventListener('popstate', handlePopstate);
      return () => {
        window.removeEventListener('popstate', handlePopstate);
      };
  }, []);
  const displayErrorsCount = (errorCount: number, touchedFields: number): void => {
    if (touchedFields === 0) {
      setActiveError(true);
    } else if (errorCount > 0) {
      setActiveError(true);
    } else {
      setActiveError(false);
    }
  };

  const clearApiError = () => {
    setApiError({
      Error: '',
    });
  };

  const postObligation = async (formikContext: any): Promise<void> => {
    window.scrollTo(0, 0);
    setLoading(true);
    // remove whitespaces before and after obligation number
    const trimmedObligationNumber = formikContext.values.OBLIGATION_NUMBER.trim();
    formikValuesRef.current.setFieldValue('OBLIGATION_NUMBER', trimmedObligationNumber);
    const payload = {
      obligationNumber: trimmedObligationNumber,
    };
    const url = `${API_URL}sitecore/Extension/ValidateObligation`;
    let response: any;
    try {
      response = await fetch(url, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify(payload),
      });
      const responseBody = await response.json();
      setLoading(false);
      if (response.status === 400 && responseBody.Error) {
        setApiError(responseBody);
        return;
      }
      if (response.status === 422 && responseBody.Error) {
        setApiError(responseBody);
        return;
      }
      if (response.status === 200) {
          getBasicInfo(formikContext.values.OBLIGATION_NUMBER, FormType.EXTENSION)
          .then((resBody) => {
            if (resBody) {
              const isInitialOrReminderStatus = resBody.Status ? (resBody.Status === 'Infringement' || resBody.Status === 'PenaltyReminder') : false;
              const isEOIW = (resBody.Status === 'NoticeOfFinalDemand' || resBody.Status === 'EnforcementWarrant');
              formikContext.setFieldValue('isEOIW', isEOIW);
              formikContext.setFieldValue('isInitialOrReminderStatus', isInitialOrReminderStatus);
              formikContext.setFieldValue('Status', resBody.Status);
              formikContext.setFieldValue('IsCompany', resBody.IsCompany);
              formikContext.setFieldValue('DDD', resBody.DDD);
              formikContext.setFieldValue('ESI', resBody.ESI);
              formikContext.setFieldValue('MarineOffence', resBody.MarineOffence);
              formikContext.setFieldValue('IsESIorDDD', resBody.IsESIorDDD);
            }
          })
          .catch((e) => console.log(e));
        const nextPageUrl = `${pathNameLessLastSegment}${responseBody}`;
        clearApiError();
        navigate(nextPageUrl);
        window.scrollTo(0, 0);
        setLoading(false);
        return;
      }
      navigateToError();
    } catch (error) {
      navigateToError();
    }
  };
  const resetPFormDataValues = (partialFormApi: any, formikObject: any) => {
    if (partialFormApi && formikObject) {
      // clone data format like objectkey: [{},{}]
      if (
        Array.isArray(partialFormApi) &&
        partialFormApi[0] &&
        typeof partialFormApi[0] === 'object' &&
        Array.isArray(formikObject) &&
        (formikObject.length !== partialFormApi.length || partialFormApi.length === 0)
      ) {
        partialFormApi.forEach((val) => {
          type dynamicObj = {
            [key: string]: string;
          };
          const temp = {} as dynamicObj;
          Object.keys(val).forEach((key) => {
            temp[key] = val[key];
          });
          formikObject.push(temp);
        });
      }
      // handle scenario if formikObject is empty array , it's for ObjectKey:[] or string[]
      if (
        Array.isArray(partialFormApi) &&
        partialFormApi[0] &&
        typeof partialFormApi[0] === 'string' &&
        Array.isArray(formikObject) &&
        (formikObject.length !== partialFormApi.length || partialFormApi.length === 0)
      ) {
        partialFormApi.forEach((val) => {
          formikObject.push(val);
        });
      }
      if (partialFormApi && formikObject) {
        Object.keys(partialFormApi).forEach((key) => {
          if (key in formikObject) {
            if (typeof partialFormApi[key] === 'object' && !(partialFormApi[key] instanceof Date)) {
              resetPFormDataValues(partialFormApi[key], formikObject[key]);
            } else {
              formikObject[key] = partialFormApi[key];
            }
          }
        });
      }
    }
  };

  const checkExistingForm = async (formikContext: any): Promise<void> => {
    setLoading(true);
    const payload = {
      ObligationNO: formikContext.values.OBLIGATION_NUMBER,
      formType: 'PaymentExtension',
    };
    const url = `${API_URL}sitecore/PartialForms/CheckingExistingForm`;

    let response: any;
    try {
      response = await fetch(url, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify(payload),
      });
      const responseBody = await response.json();
      if (response.status === 200) {
        if (responseBody.PartialFormID) formikValuesRef.current.setFieldValue('PartialFormID', responseBody.PartialFormID);
        if (responseBody.DebtorID) formikValuesRef.current.setFieldValue('DebtorID', responseBody.DebtorID, formikContext);
        if (responseBody.CheckingExisting === 'no' && responseBody.CanSave) {
          setLoading(false);
          navigate(`${pathNameLessLastSegment}/step3`);
          window.scrollTo(0, 0);
          return;
        }
        if (responseBody.PartialFormID) {
          getPartialForm(responseBody.PartialFormID)
            .then((resBody) => {
              if (typeof resBody === 'string') {
                if (resBody === 'Unauthorised') {
                  navigateToError();
                }
              } else {
                const formData = JSON.parse(resBody.FormData);
                const keys = Object.keys(resBody);
                keys.forEach((key) => {
                  if (key === 'FormData') {
                    setFormDataValues(formData, formikValuesRef.current.values.FormData, formikValuesRef, 'FormData');
                    setCurrentFormikValues(formData, formikValuesRef.current.values, formikValuesRef);
                  } else if (resBody[key]) {
                    formikContext.setFieldValue(`${key}`, resBody[key]);
                  }
                });
              }
              setLoading(false);
            })
            .catch((e) => console.log(e));
        } else {
          setLoading(false);
        }
        navigate(`${pathNameLessLastSegment}/step4`);
        window.scrollTo(0, 0);
      }
    } catch (error) {
      navigateToError();
    }
  };

  const validateDebtor = async (formikContext: any): Promise<void> => {
    window.scrollTo(0, 0);
    setLoading(true);
    // Backend api is checking date time for some reasons, time need to be 14:00:00
    const getOffenceDate = formikContext.values.OFFENCE_DATE && getDateForValidateDebtor(formikContext.values.OFFENCE_DATE.toLocaleDateString('en-AU'));
    const getHearingDate = formikContext.values.HEARING_DATE && getDateForValidateDebtor(formikContext.values.HEARING_DATE.toLocaleDateString('en-AU'));
    const getDOBDate = formikContext.values.DOB && getDateForValidateDebtor(formikContext.values.DOB.toLocaleDateString('en-AU'));
    const payload = {
      dateOfBirth: getDOBDate,
      hearingDate: getHearingDate,
      licenceNumber: formikContext.values.DRIVER_LICENCE,
      obligationNumber: formikContext.values.OBLIGATION_NUMBER,
      offenceDate: getOffenceDate,
      registrationNumber: formikContext.values.VEHICLE_REGISTRATION,
    };
    const url = `${API_URL}sitecore/Extension/ValidateDebtor`;
    let response: any;
    try {
      response = await fetch(url, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify(payload),
      });
      const responseBody = await response.json();
      setLoading(false);
      if (response.status === 400 && responseBody.Error) {
        setApiError(responseBody);
        return;
      }
      if (response.status === 200) {
        setApiError({
          Error: '',
        });
        if (responseBody === '/step3') {
          checkExistingForm(formikContext);
        }
        if (responseBody === '/step8' || responseBody === '/step9') {
          navigate(`${location.pathname.split('/').slice(0, -1).join('/')}${responseBody}`);
          window.scrollTo(0, 0);
        }
      }
    } catch (error) {
      navigateToError();
    }
  };
  const createPartialForm = async (formikContext: any): Promise<void> => {
    if (formikContext.values.SaveYesNo === 'false' || getIn(formikContext.values, 'PartialFormID')) {
      navigateToNextStep(navigate, location, 'next');
      return;
    }
    setLoading(true);
    const { values } = formikContext;
    resetPFormDataValues(values, formikContext.values.FormData);
    const payload = {
      Attachments: '',
      CreateDate: '',
      DebtorID: `${formikContext.values.DebtorID}`,
      EMRemeinderSent: false,
      Email: formikContext.values.saveEmail,
      ExpiryDate: '',
      FormData: JSON.stringify({ a: '1', ...formikContext.values.FormData }),
      FormTypeID: 2,
      ID: 0,
      IsSubscribedEM: !!formikContext.values.saveEmail,
      IsSubscribedSMS: !!formikContext.values.saveMobile,
      Mobile: formikContext.values.saveMobile,
      Obligations: [
        {
          ID: 0,
          ObligationNO: formikContext.values.OBLIGATION_NUMBER,
          IsPrimary: true,
        },
      ],
      SMSRemeinderSent: false,
    };
    const url = `${API_URL}sitecore/PartialForms/CreatePartialForm`;
    let response: any;
    try {
      response = await fetch(url, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify(payload),
      });
      const responseBody = await response.json();
      setLoading(false);
      if (response.status === 200) {
        if (responseBody.ID) {
          formikValuesRef.current.setFieldValue('PartialFormID', responseBody.ID);
          formikValuesRef.current.setFieldValue('Obligations', responseBody.Obligations);
        }
        navigateToNextStep(navigate, location, 'next');
        return;
      }
      navigateToError();
    } catch (error) {
      console.log(error);
    }
  };

  const updatePartialFormData = async (formikContext: any): Promise<boolean> => {
    const { values } = formikContext;
    resetPFormDataValues(values, formikContext.values.FormData);
    const partialformPayload: iPartialFormData = {
      ID: formikContext.values.PartialFormID,
      Obligations: formikContext.values.Obligations,
      Attachments: [],
      FormData: JSON.stringify(formikContext.values.FormData), // {a: '1'},formikContext.values.FormData
      DebtorID: formikContext.values.DebtorID,
      FormTypeID: 2,
      Email: formikContext.values.saveEmail,
      Mobile: formikContext.values.saveMobile,
      IsSubscribedEM: formikContext.values.IsSubscribedEM,
      IsSubscribedSMS: formikContext.values.IsSubscribedSMS,
      EMRemeinderSent: formikContext.values.EMRemeinderSent,
      SMSRemeinderSent: formikContext.values.SMSRemeinderSent,
      CreateDate: formikContext.values.CreateDate,
      ExpiryDate: formikContext.values.ExpiryDate,
    };
    try {
      const response = await savePartialForm(partialformPayload);
      return response;
    } catch (error) {
      console.log(error);
      return false;
    }
  };
  const createPartialFormForSumbit = async (formValues: iExtensionFormValues): Promise<string> => {
    formikValuesRef.current.setFieldValue('SaveYesNo', 'true');
    resetPFormDataValues(formikValuesRef.current.values, formValues.FormData);
    const payload = {
      Attachments: '',
      CreateDate: '',
      DebtorID: `${formValues.DebtorID}`,
      EMRemeinderSent: false,
      Email: '',
      ExpiryDate: '',
      FormData: JSON.stringify({ ...getIn(formValues, 'FormData') }),
      FormTypeID: 2,
      ID: 0,
      IsSubscribedEM: false,
      IsSubscribedSMS: false,
      Mobile: '',
      Obligations: [
        {
          ID: 0,
          ObligationNO: formValues.OBLIGATION_NUMBER,
          IsPrimary: true,
        },
      ],
      SMSRemeinderSent: false,
    };
    const url = `${API_URL}sitecore/PartialForms/CreatePartialForm`;
    let response: any;
    try {
      response = await fetch(url, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify(payload),
      });
      const responseBody = await response.json();
      if (responseBody.status === 200) {
          return 'true';
      }
      return responseBody;
    } catch (error) {
      return `${error}`;
    }
  };
  const submitForm = async (formValues: iExtensionFormValues): Promise<boolean> => {
    setLoading(true);
    submitSavedPartialForm(formValues.PartialFormID.toString());
    /* const receiptPayload = getReceiptPayload(formValues);
    const urlReceipt = `${API_URL}sitecore/Receipt/CreateReceiptNumber`;
    let receiptResponse: any;
    try {
      receiptResponse = await fetch(urlReceipt, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: receiptPayload,
      });
    } catch (error) {
      return false;
    }

    if (receiptResponse.status !== 200) {
      return false;
    }

    const submissionNumber = await receiptResponse.text();
    */
    const submissionNumber = 'SubmissionNumber';
    formikValuesRef.current.setFieldValue('submissionNo', submissionNumber);
    const formSubmissionValues = getSubmissionPayload(formValues, submissionNumber);
    const submitPayload = {
      data: JSON.stringify(formSubmissionValues),
      obligationNo: formValues.OBLIGATION_NUMBER
    };

    const urlSubmission = `${API_URL}sitecore/Extension/SubmitExtensionOfTime`;
    let submitResponse = false;
      submitResponse = await fetch(urlSubmission, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify(submitPayload),
      })
    .then((formSubResponse) => {
      if (formSubResponse.status === 200) {
        if (formValues.SaveYesNo === 'true') {
          // having partial form, close it
          submitSavedPartialForm(formValues.PartialFormID.toString());
        }
        formikValuesRef.current.setFieldValue('submitted', true);
        ReactGA.event({
          category: 'Extension Form',
          action: 'Submit',
          label: 'Submit Extension Form',
        });
        return formSubResponse.json();
      }
      formikValuesRef.current.setFieldValue('submissionError', formSubResponse.statusText);
      if (formValues.SaveYesNo === 'false') {
        createPartialFormForSumbit(formValues);
      }
      navToError(navigate, location);
      return false;
    }).then((response) => {
      if (response) {
        formikValuesRef.current.setFieldValue('submissionNo', response.receiptNo); // get submission number from API response
        return true;
      }
      return false;
    })
    .catch((error) => {
      formikValuesRef.current.setFieldValue('submissionError', error);
      if (formValues.SaveYesNo === 'false') {
        createPartialFormForSumbit(formValues);
      }
      navToError(navigate, location);
      return false;
    })
.finally(() => {
      setLoading(false);
    });
    if (!submitResponse) return false;
    return true;
  };
  const step4NextPage = (formikContext: any) => {
    if (formikContext.values.SaveYesNo === 'true') {
      updatePartialFormData(formikContext);
    }
    window.scrollTo(0, 0);
    navigateToNextStep(navigate, location, 'next');
  };
  const step5NextPage = (formikContext: any) => {
    if (formikContext.values.SaveYesNo === 'true') {
      updatePartialFormData(formikContext);
    }
    window.scrollTo(0, 0);
    navigateToNextStep(navigate, location, 'next');
  };
  const step6NextPage = (formikContext: any) => {
    if (formikContext.values.SaveYesNo === 'true') {
      updatePartialFormData(formikContext);
    }
    window.scrollTo(0, 0);
    navigateToNextStep(navigate, location, 'next');
  };
  const step7NextPage = (formikContext: any) => {
    if (formikContext.values.SaveYesNo === 'true') {
      updatePartialFormData(formikContext);
    }
    window.scrollTo(0, 0);
    navigateToNextStep(navigate, location, 'next');
  };

  const formNavActions: iFormNavActions = {
    step1: {
      next: postObligation,
    },
    step2: {
      next: validateDebtor,
    },
    step3: {
      next: createPartialForm,
    },
    step4: {
      next: step4NextPage,
    },
    step5: {
      next: step5NextPage,
    },
    step6: {
      next: step6NextPage,
    },
    step7: {
      next: step7NextPage,
    },
  };

  return (
    <Formik
      initialValues={defaultForm()}
      innerRef={formikValuesRef}
      validationSchema={ExtensionSchema}
      onSubmit={(values: iExtensionFormValues) => {
        submitForm(values);
      }}>
      {({ values, touched, errors }) => (
        <VicFinesContext.Provider value={{ countryList, setCountryList, tooltips, setTooltips, apiError, clearApiError }}>
          <FormContainer title="Extension Form">
            {loading && <FormLoading />}
            {!loading && (
              <Form>
                {!location.pathname.includes('error') && <ProgressBar formMetaData={extensionMetaData} />}
                {activeError && <Alert title="Sorry there is a problem" icon={IssueIcon} color="red" message={<ErrorContent />} theme="error" />}
                <Routes>
                  <Route path="/step0/:ob" element={<ExtensionStep0 formikRef={formikValuesRef} />} />
                  <Route path="/step1" element={<ExtensionStep1 />} />
                  <Route path="/step2" element={<ExtensionStep2 />} />
                  <Route path="/step3" element={<ExtensionStep3 />} />
                  <Route path="/step4" element={<ExtensionStep4 />} />
                  <Route path="/step5" element={<ExtensionStep5 />} />
                  <Route path="/step6" element={<ExtensionStep6 />} />
                  <Route path="/step7" element={<ExtensionStep7 />} />
                  <Route path="/step8" element={<ExtensionStep8 />} />
                  <Route path="/step9" element={<ExtensionStep9 />} />
                  <Route path="/step10" element={<ExtensionStep10 />} />
                  <Route path="/step11" element={<ExtensionStep11 />} />
                  <Route path="/step12" element={<ExtensionStep12 />} />
                  <Route path="/step15" element={<ExtensionStep15 />} />
                  <Route path="/step16" element={<ExtensionStep16 />} />
                  <Route path="/error" element={<ExtensionErrorPage />} />
                  <Route path="/" element={<ExtensionStep0 formikRef={formikValuesRef} />} />
                </Routes>
                {!(
                  location.pathname === `${PATH_EXTENSION}/step10` ||
                  location.pathname === `${PATH_EXTENSION}/step16` ||
                  location.pathname === `${PATH_EXTENSION}/step17` ||
                  location.pathname === `${PATH_EXTENSION}/error`
                ) && (
                  <FormNavFooter
                    totalPages={16}
                    formNavActions={formNavActions}
                    submitPage={9}
                    submit={async () => submitForm(values)}
                    tooltips={tooltips}
                    displayErrorsCount={displayErrorsCount}
                    pageValidationKeys={extensionMetaData.pagesSegments[currentStep].validationKeys(values)}
                    formType={FormType.EXTENSION}
                    firstPageNo={0}
                  />
                )}
              </Form>

            )}
          </FormContainer>
        </VicFinesContext.Provider>
      )}
    </Formik>
  );
}

export default Extension;
